import { useCallback, type ReactElement } from 'react'

import { useTranslation } from 'react-i18next'
import type { ViewProductGpsrInfoStore } from '../../../AppData/_stores/ViewProductStore'
import {
  InfoField,
  InfoLabel,
  InfoSection,
  InfoSectionFields,
  InfoSectionTitle,
  InfoValue,
  ProductInfoViewProductList,
  type RenderedProductInfoViewProduct
} from './ProductInfoLayout'
import useProductInfoViewProducts from './useProductInfoViewProducts'

interface Props {
  idProduct: number
}

export default function ProductMaterialsTab(props: Props) {
  const [t] = useTranslation('account-v2')
  const viewProducts = useProductInfoViewProducts(props.idProduct)

  const getMaterialsContent = useCallback((info: ViewProductGpsrInfoStore): ReactElement | null => {
    if (!info.get('material')
      && !info.get('material_certification')
      && !info.get('raw_material_source')
      && !info.get('raw_material_address')
      && !info.get('care_instructions')) {
      return null
    }

    return <>
      {info.get('material') || info.get('material_certification') ? <InfoSection>
        <InfoSectionTitle>{t('Composition')}</InfoSectionTitle>
        <InfoSectionFields>
          {info.get('material') ? <InfoField>
            <InfoLabel>{t('Material')}</InfoLabel>
            <InfoValue>{info.get('material')}</InfoValue>
          </InfoField> : null}
          {info.get('material_certification') ? <InfoField>
            <InfoLabel>{t('Material certification')}</InfoLabel>
            <InfoValue>{info.get('material_certification')}</InfoValue>
          </InfoField> : null}
        </InfoSectionFields>
      </InfoSection> : null}

      {info.get('raw_material_source') || info.get('raw_material_address') ? <InfoSection>
        <InfoSectionTitle>{t('Raw material source')}</InfoSectionTitle>
        <InfoSectionFields>
          {info.get('raw_material_source') ? <InfoField>
            <InfoLabel>{t('Source')}</InfoLabel>
            <InfoValue>{info.get('raw_material_source')}</InfoValue>
          </InfoField> : null}
          {info.get('raw_material_address') ? <InfoField>
            <InfoLabel>{t('Address')}</InfoLabel>
            <InfoValue>{info.get('raw_material_address')}</InfoValue>
          </InfoField> : null}
        </InfoSectionFields>
      </InfoSection> : null}

      {info.get('care_instructions') ? <InfoSection>
        <InfoSectionTitle>{t('Care')}</InfoSectionTitle>
        <InfoSectionFields>
          <InfoField className="wide">
            <InfoValue>{info.get('care_instructions')}</InfoValue>
          </InfoField>
        </InfoSectionFields>
      </InfoSection> : null}
    </>
  }, [t])

  const renderedViewProducts = viewProducts.reduce<RenderedProductInfoViewProduct[]>((list, viewProduct) => {
    const info = viewProduct.get('gpsrInfo')
    if (!info) return list

    const renderedContent = getMaterialsContent(info)
    if (!renderedContent) return list

    list.push({
      renderedContent,
      viewProduct
    })

    return list
  }, [])

  return <ProductInfoViewProductList
    emptyMessage={t('No material information is available for this product yet.')}
    renderedViewProducts={renderedViewProducts}
  />
}
